import { pdf } from "@react-pdf/renderer";
import { useQueryClient } from "@tanstack/react-query";
import { Timesheet } from "domain/entities/timesheet";
import { TimesheetDay } from "domain/entities/timesheet-day";
import { EMAIL } from "domain/enum/email";
import { AbscenceTypeInputFilter } from "domain/models/abscence-type-input-filter";
import { DocumentTypeInputFilter } from "domain/models/document-type-input-filter";
import { PurchaseOrderInputFilter } from "domain/models/purchase-order-input-filter";
import { DocumentInputFilter } from "domain/models/document-input-filter";
import { TimesheetDayInputFilter } from "domain/models/timesheet-day-input-filter";
import moment from "moment";
import "moment/locale/pt";
import InputTextComponent from "presentation/components/Shared/InputTextComponent";
import Spinner from "presentation/components/Shared/Spinner";
import TimesheetReportPDF from "presentation/components/Timesheet/TimesheetReportPDF";
import { useDocumentByFilter } from "presentation/hooks/use-query-document";
import { useAbscenceTypesByFilter } from "presentation/hooks/use-query-abscence-type";
import { useDocumentTypeByFilter } from "presentation/hooks/use-query-document-type";
import { useEmployeesByFilter } from "presentation/hooks/use-query-employees";
import { usePurchaseOrdersByFilter } from "presentation/hooks/use-query-purchase-order";
import {
  useTimesheetsById,
  useUpdateTimesheetMutation,
} from "presentation/hooks/use-query-timesheet";
import {
  useCreateAllTimesheetDayMutation,
  useCreateTimesheetDayMutation,
  useDeleteTimesheetDayMutation,
  useTimesheetDaysByFilter,
  useUpdateTimesheetDayMutation,
} from "presentation/hooks/use-query-timesheet-day";
import { useTimesheetReportsByEmployee } from "presentation/hooks/use-query-timesheet-report";
import { useTimesheetUploader } from "presentation/hooks/useTimesheetUploader";
import { useTimesheetFormStore } from "presentation/stores/Timesheet/use-timesheet-form-store";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { EventType } from "presentation/stores/use-timesheet-calendar-store";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";
import { Calendar, momentLocalizer, View } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useNavigate, useParams } from "react-router-dom";
import TimesheetDayForm from "../TimesheetDayForm";
import "./index.css";
import ExtraHourTimesheetDayForm from "../ExtraHourTimesheetDayForm";

export const abscenceTypesInDays = [
  "Licença por Falecimento",
  "Licença Parental",
  "Licença Casamento",
  "Feriado",
  "Férias",
  "Em Admissão",
  "Compensação",
  "Período não trabalhado",
  "Dia de Descanso (Colaboradores de Suporte)",
  "Cessação de Contrato"
];

/**
 * Componente principal para a visualização e gestão de um Timesheet em formato de calendário.
 *
 * Responsabilidades:
 * - Exibir os registos diários num `react-big-calendar`.
 * - Gerir a navegação entre meses e a visualização (mês, semana, dia).
 * - Orquestrar as ações de adicionar, editar, apagar e copiar registos.
 * - Controlar o estado de carregamento e processamento com um spinner.
 */
const TimesheetCalendar: React.FC<{ isDisable: boolean }> = ({ isDisable }) => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const { id, employeeId } = useParams();
  const { userEmployee, email: userEmployeeEmail } = useAuthStore();
  const queryClient = useQueryClient();

  const [currentTimesheetForm, setCurrentTimesheetForm] = useState<Timesheet>();
  const [calendarEvents, setCalendarEvents] = useState<EventType[]>([]);
  const [calendarEventsWithEmpty, setCalendarEventsWithEmpty] = useState<
    EventType[]
  >([]);
  const {
    currentTimesheet,
    currentTimesheetDay,
    setCurrentTimesheet,
    setCurrentTimesheetDay,
    setCurrentEvents,
    resetTimesheetForm,
    resetCurrentTimesheetDay,
  } = useTimesheetFormStore();

  // Queries
  const { data: timesheetData, isSuccess: isTimesheetLoaded } =
    useTimesheetsById(id);
  const { data: purchaseOrderData, isSuccess: isPurchaseOrdersLoaded } = usePurchaseOrdersByFilter({
    limit: 100,
    sortBy: "name",
    sortOrder: "asc",
    employeeId: employeeId ?? userEmployee?.id,
  } as PurchaseOrderInputFilter);
  const { data: abscencesData } = useAbscenceTypesByFilter({
    limit: 100,
    sortBy: "name",
    sortOrder: "asc",
    contractType: userEmployee?.contractType,
  } as AbscenceTypeInputFilter);

  const { data: docTypeData } = useDocumentTypeByFilter({
    limit: 1,
    name: "Mapa de Horas",
  } as DocumentTypeInputFilter);

  const { data: existingDocuments } = useDocumentByFilter({
    employeeId: userEmployee?.id,
    documentTypeId: docTypeData?.data?.[0]?.id,
    source: "other",
    limit: 100,
  } as DocumentInputFilter); // Add this line

  const {
    data: timesheetDayData,
    isSuccess: isTimesheetDayLoaded,
    isLoading,
    isRefetching,
    refetch: refetchTimesheetDays,
  } = useTimesheetDaysByFilter({
    limit: 100,
    sortBy: "day",
    sortOrder: "asc",
    timesheet: currentTimesheet?.id,
  } as TimesheetDayInputFilter);
  const { mutate: updateTimesheetMutation, isSuccess: isSuccessChangeStatus } =
    useUpdateTimesheetMutation();
  const updateTimesheetDay = useUpdateTimesheetDayMutation();
  const createTimesheetDay = useCreateTimesheetDayMutation();
  const createAllTimesheetDays = useCreateAllTimesheetDayMutation();
  const deleteTimesheetDay = useDeleteTimesheetDayMutation(); // Retorna uma Promise
  const { processSingleReport, deleteDocumentMutation } =
    useTimesheetUploader();
  const { data: employeeData } = useEmployeesByFilter({
    email: userEmployee?.emailAddress ?? "",
  });
  const { data: timesheetReportData, refetch: refetchReportData } = useTimesheetReportsByEmployee(
    currentTimesheet?.id ?? ""
  );

  // States
  const [showModal, setShowModal] = useState(false);
  const [showExtraHourModal, setShowExtraHourModal] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState<EventType | undefined>();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [view, setView] = useState<"month" | "week" | "day">("month");
  const [isProcessing, setIsProcessing] = useState(false);
  const [copyRequest, setCopyRequest] = useState<{
    active: boolean;
    date: Date | null;
    timesheetDayForm?: TimesheetDayForm | null;
  }>({ active: false, date: null });

  // Calendar translator messages
  moment.locale("pt");
  const locale = momentLocalizer(moment);
  const messages = {
    allDay: "Dia inteiro",
    previous: "Anterior",
    next: "Próximo",
    today: "Hoje",
    month: "Mês",
    week: "Semana",
    day: "Dia",
    agenda: "Agenda",
    date: "Data",
    time: "Hora",
    event: "Evento",
    noEventsInRange: "Não há eventos neste período.",
    showMore: (total: number) => `+${total} mais`,
  };

  useEffect(() => {
    if (timesheetData?.data && isTimesheetLoaded) {
      setCurrentTimesheet({ ...timesheetData.data });
      setCurrentTimesheetForm({ ...timesheetData.data });
    }
  }, [timesheetData, isTimesheetLoaded]);

  /**
   * Efeito principal que processa os dados brutos da API (`timesheetDayData`)
   * e os transforma em eventos para o calendário sempre que os dados são recarregados.
   */
  useEffect(() => {
    const fn = async () => {
      if (currentTimesheet && timesheetDayData?.data) {
        let timesheetFormated = await loadData(
          timesheetDayData?.data,
          currentTimesheet
        );
        setCalendarEventsWithEmpty(timesheetFormated || []);
        timesheetFormated = timesheetFormated?.filter(
          (item) => item.title !== ""
        );
        const timesheetRecalculated = await calculateDateTime(
          timesheetFormated
        );
        setCalendarEvents([...(timesheetRecalculated || [])]);
        setIsUpdated(true);
      } else {
        setCalendarEvents([]);
        setCalendarEventsWithEmpty([]);
        setIsUpdated(true);
      }
      // Garante que o spinner é desativado após o processamento dos dados.
      setIsProcessing(false);
    };

    if (
      currentTimesheet?.year !== currentDate.getFullYear() ||
      currentTimesheet.month !== currentDate.getMonth() + 1
    ) {
      setCurrentDate(
        new Date(
          currentTimesheet?.year
            ? currentTimesheet?.year
            : currentDate.getFullYear(),
          currentTimesheet?.month
            ? currentTimesheet?.month - 1
            : currentDate.getMonth(),
          1
        )
      );
    }

    if (!isDisable) setIsDisabled(false);
    const editedStatus = ["Editado", "Novo"];
    if (!currentTimesheet || !editedStatus.includes(currentTimesheet.status))
      setIsDisabled(true);
    if (isTimesheetDayLoaded) fn();
  }, [timesheetDayData, isTimesheetDayLoaded]);

  /**
   * Efeito "portão" que observa um pedido de cópia (`copyRequest`).
   * Ele só executa a lógica de cópia quando o pedido está ativo e os dados
   * do calendário (`calendarEvents`) foram atualizados, evitando "race conditions".
   */
  useEffect(() => {
    if (copyRequest.active && copyRequest.date && isUpdated) {
      // Este efeito funciona como um "portão". Ele só executa a cópia
      // quando o pedido está ativo E os eventos do calendário (possivelmente atualizados) estão disponíveis.
      const sourceDate = new Date(copyRequest.date); // Evita mutação do estado
      const startOfDay = new Date(new Date(sourceDate).setHours(0, 0, 0, 0));
      const endOfDay = new Date(new Date(sourceDate).setHours(23, 59, 59, 999));

      let currentId = "";
      if (copyRequest.timesheetDayForm?.purchaseOrder?.id) {
        currentId = copyRequest.timesheetDayForm?.purchaseOrder?.id;
      } else if (copyRequest.timesheetDayForm?.abscence?.id) {
        currentId = copyRequest.timesheetDayForm?.abscence?.id;
      }
      const recordsToCopy = calendarEvents?.filter(
        (item) =>
          item.start >= startOfDay &&
          item.start <= endOfDay &&
          item.title !== "" &&
          item.eventId === currentId
      );

      console.log(recordsToCopy, copyRequest.timesheetDayForm);

      // Apenas prossegue e reseta o pedido se encontrar registos para copiar.
      // Se não encontrar, espera por uma nova atualização de `calendarEvents` que pode conter os dados.
      if (recordsToCopy && recordsToCopy.length > 0) {
        handleCopyToRestOfMonth(sourceDate, recordsToCopy);
        // Reseta o pedido para evitar execuções repetidas
        setCopyRequest({ active: false, date: null });
      }
      setIsUpdated(false); // Reseta o estado de atualização para aguardar novos dados
    }
  }, [calendarEvents, copyRequest, isUpdated]); // Executa quando os eventos do calendário são atualizados

  /**
   * Transforma os dados brutos de `TimesheetDay` da API em `EventType`
   * para serem consumidos pelo componente `Calendar`.
   * @param {TimesheetDay[]} tsdData - Dados dos dias da timesheet.
   * @param {Timesheet} tsData - Dados da timesheet pai.
   * @returns {Promise<EventType[] | undefined>} Uma lista de eventos formatados.
   */
  const loadData = async (
    tsdData: TimesheetDay[],
    tsData: Timesheet
  ): Promise<EventType[] | undefined> => {
    let timesheetFormated = [];
    if (tsdData && tsData) {
      for (let tsCount = 0; tsCount < tsdData.length; tsCount++) {
        const item = tsdData[tsCount];
        let currentDate = new Date(tsData?.year, tsData?.month - 1, item.day);
        if (item.abscenceType?.id) {
          const title = abscencesData?.data?.find(
            (abscence) => abscence.id === item.abscenceType?.id
          );
          let abscenceEvent: EventType = {
            id: item.id,
            eventId: item.abscenceType.id,
            hours: item.abscenceTime ?? 0,
            title: title?.name ?? "",
            type: "abscence",
            start: new Date(currentDate),
            end: new Date(currentDate),
          };

          timesheetFormated.push(abscenceEvent);
        }

        if (item.purchaseOrders && item.purchaseOrders?.length > 0) {
          item.purchaseOrders.forEach((subitem) => {
            const title = purchaseOrderData?.data?.find(
              (purchaseOrder) => purchaseOrder.id === subitem.id
            );
            timesheetFormated.push({
              id: item.id,
              eventId: subitem.id ?? "",
              hours: subitem.hours,
              title: title?.name ?? "",
              type: title?.type == "Hora Extra" ? "extraHour" : "project",
              start: new Date(currentDate),
              end: new Date(currentDate),
            });
          });
        }

        if (
          !item.abscenceType ||
          !item.purchaseOrders ||
          item.purchaseOrders?.length <= 0
        ) {
          let emptyEvent: EventType = {
            id: item.id,
            eventId: "",
            hours: 0,
            title: "",
            type: "",
            start: new Date(currentDate),
            end: new Date(currentDate),
          };
          timesheetFormated.push(emptyEvent);
        }
      }
    }
    return timesheetFormated;
  };

  // Alterna entre o mode view e edit
  useEffect(() => {
    setIsDisabled(isDisable);
  }, [isDisable]);

  const extraHourPurchaseOrders =
    purchaseOrderData?.data?.filter(
      po => po.type?.toLowerCase() === "hora extra"
    ) ?? [];

  let hasExtraHour = extraHourPurchaseOrders.length > 0;

  /**
   * Efeito que lida com o sucesso da mutação de mudança de status da timesheet.
   * Exibe uma notificação e redireciona o utilizador.
   */
  useEffect(() => {
    if (isSuccessChangeStatus) {
      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: "Mapa de horas concluído",
        life: 1500,
      });
      setTimeout(() => {
        resetCurrentTimesheetDay();
        navigate("/timesheets");
      }, 2000);
    }
  }, [isSuccessChangeStatus]);

  /**
   * Manipulador para a navegação do calendário (mês anterior/seguinte).
   * @param {Date} newDate - A nova data para a qual navegar.
   */
  const handleNavigate = async (newDate: Date) => {
    setCurrentDate(newDate);
  };

  const handleAddExtraHourEvent = (date: Date) => {
    setSelectedEvent(undefined);

    const timesheetDateFiltered = timesheetDayData?.data?.find(
      (item) => item.day === date.getDate()
    );
    resetCurrentTimesheetDay();
    if (timesheetDateFiltered) {
      setCurrentTimesheetDay(timesheetDateFiltered);
    }

    setSelectedDate(date);
    setShowExtraHourModal(true);
  };

  /**
   * Abre o modal para adicionar um novo registo para uma data específica.
   * @param {Date} date - A data para a qual o registo será adicionado.
   */
  const handleAddEvent = (date: Date) => {
    setSelectedEvent(undefined);

    if (
      (date.getDay() === 0 ||
        date.getDay() === 6 ||
        date.getMonth() + 1 !== currentTimesheet?.month) &&
      !userEmployee?.isSuportService
    )
      return;

    // Verificar se o dia tem férias
    const hasFeriasInDay = timesheetDayData?.data?.some(
      (day) => day.day === date.getDate() && day.abscenceType?.name?.toLowerCase() === "férias"
    );
    if (hasFeriasInDay) return;

    const timesheetDateFiltered = timesheetDayData?.data?.find(
      (item) => item.day === date.getDate()
    );
    resetCurrentTimesheetDay();
    if (timesheetDateFiltered) {
      setCurrentTimesheetDay(timesheetDateFiltered);
    }

    setSelectedDate(date);
    setShowModal(true);
  };

  /**
   * Copia os registos de um dia para o próximo dia útil.
   * Lida com a lógica de encontrar o próximo dia válido e confirmações de sobrescrita.
   * @param {Date} date - A data de origem dos registos a serem copiados.
   */
  const handleReplyNextDay = async (date: Date) => {
    const startOfDay = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      0,
      0,
      0
    );
    const endOfDay = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23,
      59,
      59
    );

    const recordsToCopy = calendarEvents?.filter(
      (item) => item.start >= startOfDay && item.start <= endOfDay
    );

    if (!recordsToCopy || recordsToCopy.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Advertência",
        detail: `Não existem registos para copiar.`,
        life: 1500,
      });
      return;
    }

    let nextDay = new Date(date);
    let isWorkDayFound = false;

    while (!isWorkDayFound) {
      nextDay.setDate(nextDay.getDate() + 1);

      const nextDayIsWorkDay =
        userEmployee?.isSuportService ||
        (nextDay.getMonth() === date.getMonth() &&
          nextDay.getDay() !== 0 &&
          nextDay.getDay() !== 6);

      if (nextDayIsWorkDay) {
        const nextStartOfDay = new Date(
          nextDay.getFullYear(),
          nextDay.getMonth(),
          nextDay.getDate(),
          0,
          0,
          0
        );
        const nextEndOfDay = new Date(
          nextDay.getFullYear(),
          nextDay.getMonth(),
          nextDay.getDate(),
          23,
          59,
          59
        );

        const timesheetDayId = timesheetDayData?.data?.find(
          (item) => item.day === nextDay.getDate()
        );

        const existingNextDayEvents = calendarEvents?.filter(
          (item) => item.start >= nextStartOfDay && item.start <= nextEndOfDay
        );

        if (existingNextDayEvents && existingNextDayEvents.length > 0) {
          const hasHolidays = existingNextDayEvents.some(
            (currentDay) => currentDay.title === "Feriado" || currentDay.title?.toLowerCase() === "férias"
          );
          if (hasHolidays) {
            toast.current?.show({
              severity: "warn",
              summary: "Advertência",
              detail: `Já existem registos de feriado ou férias no dia ${nextDay.toLocaleDateString()}.`,
              life: 2000,
            });
            return;
          }
        }

        const applyCopy = async () => {
          const copiedEvents = recordsToCopy?.map((record) => ({
            ...record,
            id: timesheetDayId?.id ?? undefined,
            start: new Date(
              nextDay.getFullYear(),
              nextDay.getMonth(),
              nextDay.getDate(),
              record.start.getHours(),
              record.start.getMinutes(),
              record.start.getSeconds()
            ),
            end: new Date(
              nextDay.getFullYear(),
              nextDay.getMonth(),
              nextDay.getDate(),
              record.end.getHours(),
              record.end.getMinutes(),
              record.end.getSeconds()
            ),
          }));

          const eventsWithoutNextDay = calendarEvents?.filter(
            (item) => item.start < nextStartOfDay || item.start > nextEndOfDay
          );

          const eventsUpdated = [
            ...(eventsWithoutNextDay ?? []),
            ...(copiedEvents ?? []),
          ];

          saveReplyNextDay(copiedEvents ?? []);

          const timesheetCalculated = await calculateDateTime(eventsUpdated);
          setCalendarEvents(timesheetCalculated || []);
        };
        if (existingNextDayEvents && existingNextDayEvents.length > 0) {
          confirmAction(
            `Já existem registos no dia ${nextDay.toLocaleDateString()}. Deseja sobrescrever?`,
            async () => {
              await applyCopy();
            }
          );
        } else {
          await applyCopy();
        }

        isWorkDayFound = true;
      }

      if (
        !userEmployee?.isSuportService &&
        nextDay.getMonth() !== date.getMonth()
      ) {
        isWorkDayFound = true;
      }
    }
  };

  /**
   * Copia os registos de um dia para todos os dias úteis restantes do mês.
   * Executa as operações de salvamento de forma sequencial para não sobrecarregar o backend.
   * @param {Date} sourceDate - A data de origem dos registos.
   * @param {EventType[]} recordsToCopy - A lista de eventos a serem copiados.
   */
  const handleCopyToRestOfMonth = async (
    sourceDate: Date,
    recordsToCopy: EventType[]
  ) => {
    let hasError = false;
    setIsProcessing(true);
    // Cria novas instâncias de Date para evitar mutação do estado original
    const safeSourceDate = new Date(sourceDate);

    if (!recordsToCopy || recordsToCopy.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Aviso",
        detail: "Nenhum registo encontrado neste dia.",
        life: 2000,
      });
      setIsProcessing(false);
      return;
    }

    const totalDays = coundDaysInMonth();
    const timesheetDaysToProcess: TimesheetDay[] = [];

    for (let day = safeSourceDate.getDate() + 1; day <= totalDays; day++) {
      const timesheetDayId = timesheetDayData?.data?.find(
        (item) => item.day === day
      );

      const dateToApply = new Date(
        safeSourceDate.getFullYear(),
        safeSourceDate.getMonth(),
        day
      );

      if (
        userEmployee?.isSuportService ||
        (dateToApply.getDay() !== 0 && dateToApply.getDay() !== 6)
      ) {
        const startOfTargetDay = new Date(
          new Date(dateToApply).setHours(0, 0, 0, 0)
        );
        const endOfTargetDay = new Date(
          new Date(dateToApply).setHours(23, 59, 59, 999)
        );

        // Verifica se o dia de destino já tem algum registo que não seja feriado, férias ou vazio
        const hasNonHolidayEvents = calendarEvents.some(
          (ev) =>
            ev.start >= startOfTargetDay &&
            ev.start <= endOfTargetDay &&
            ev.title !== "Feriado" &&
            ev.title?.toLowerCase() !== "férias" &&
            ev.title !== ""
        );

        if (!hasNonHolidayEvents) {
          // Constrói o objeto TimesheetDay para este dia, garantindo que ambos os tipos de registo são incluídos.
          const timesheetDayObject: TimesheetDay = {
            id: timesheetDayId?.id ?? undefined,
            day: day,
            timesheet: { id: currentTimesheet?.id },
            lastModifiedBy: userEmployeeEmail ?? EMAIL,
            // Inicializa purchaseOrders como um array vazio para poder adicionar registos de projeto.
            purchaseOrders: [],
          };

          // Itera sobre os registos a copiar e preenche o objeto.
          recordsToCopy.forEach((record) => {
            if (record.type === "abscence") {
              // Adiciona o registo de ausência.
              timesheetDayObject.abscenceType = {
                id: record.eventId,
                hours: record.hours || 0,
                name: record.title,
              };
              timesheetDayObject.abscenceTime = record.hours || 0;
            } else {
              // Adiciona registos de projeto ao array.
              timesheetDayObject.purchaseOrders?.push({
                id: record.eventId,
                name: record.title,
                hours: record.hours || 0,
              });
            }
          });

          timesheetDaysToProcess.push(timesheetDayObject);
        }
      }
    }

    try {
      // Executa a operação de criação/atualização em massa
      await createAllTimesheetDays(timesheetDaysToProcess);

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: "Registos copiados para os dias úteis restantes.",
        life: 3000,
      });
    } catch (error) {
      hasError = true;
      toast.current?.show({
        severity: "error",
        summary: "Erro na Cópia",
        detail: "Ocorreu um erro ao copiar um ou mais registos.",
        life: 3000,
      });
    } finally {
      // Invalida a query para recarregar os dados do calendário,
      // independentemente de a cópia ter tido sucesso ou falha.
      queryClient.invalidateQueries({ queryKey: ["timesheetDays"] });
      if (hasError) {
        // Se houve um erro, o useEffect que desliga o spinner pode não ser
        // acionado se os dados não mudarem, então garantimos que ele é desligado aqui.
        setIsProcessing(false);
      }
    }
  };

  /**
   * Função auxiliar que constrói o objeto `TimesheetDay` e chama a mutação de criação ou atualização.
   * @param {EventType[]} events - A lista de eventos para um dia específico a ser salvo.
   * @returns {Promise<any>} A promessa da mutação.
   */
  const saveReplyNextDay = (events: EventType[]): Promise<any> => {
    let timesheetDayObject: TimesheetDay = {
      id: events[0].id && events[0].id !== "" ? events[0].id : undefined,
      day: events[0].start.getDate(),
      timesheet: {
        id: currentTimesheet?.id,
      },
      lastModifiedBy: userEmployeeEmail ?? EMAIL,
    };

    events.forEach((event) => {
      if (event.type === "abscence") {
        timesheetDayObject.abscenceType = {
          id: event.eventId,
          hours: event.hours || 0,
          name: event.title,
        };
        timesheetDayObject.abscenceTime = event.hours || 0;
      } else {
        if (!timesheetDayObject.purchaseOrders) {
          timesheetDayObject.purchaseOrders = [];
        }
        timesheetDayObject.purchaseOrders.push({
          id: event.eventId,
          name: event.title,
          hours: event.hours || 0,
        });
      }
    });
    if (timesheetDayObject) {
      if (timesheetDayObject.id) {
        return updateTimesheetDay(timesheetDayObject);
      } else {
        return createTimesheetDay(timesheetDayObject);
      }
    }
    return Promise.resolve(); // Retorna uma promise resolvida se não houver nada a fazer
  };

  /**
   * Fecha o modal de registo de horas.
   */
  const handleCloseModal = () => {
    setShowModal(false);
    resetCurrentTimesheetDay();
  };

  const handleCloseExtraHourModal = () => {
    setShowExtraHourModal(false);
    resetCurrentTimesheetDay();
  };

  /**
   * Manipulador chamado após o formulário ser salvo com sucesso.
   * @param {object} options - Opções do salvamento, incluindo se deve aplicar a todos os dias.
   */
  const handleAdd = async (options: {
    applyToAll: boolean;
    timesheetDayForm?: TimesheetDayForm | null;
  }) => {
    handleCloseModal();
    handleCloseExtraHourModal();
    if (options.applyToAll && selectedDate) {
      // Ativa um estado para que o useEffect correspondente execute a cópia
      refetchTimesheetDays().then(() => {
        setIsUpdated(false);
        setCopyRequest({
          active: true,
          date: selectedDate,
          timesheetDayForm: options.timesheetDayForm,
        });
      });
      return;
    }
    await refetchTimesheetDays();
  };

  /**
   * Manipulador chamado após um registo ser apagado com sucesso.
   */
  const handleDelete = async () => {
    await refetchTimesheetDays();
    handleCloseModal();
    handleCloseExtraHourModal();
  };

  /**
   * Manipulador para a mudança de visualização do calendário (mês, semana, dia).
   * @param {View} newView - A nova visualização selecionada.
   */
  const handleViewChange = (newView: View) => {
    if (["month", "week", "day"].includes(newView)) {
      setView(newView as "month" | "week" | "day");
    }
  };

  /**
   * Abre o modal para editar um evento existente.
   * @param {any} event - O evento clicado no calendário.
   */
  const handleEdit = (event: any) => {
    // Não permitir edição de eventos de férias
    if (event.title.toLowerCase() === "férias") {
      return;
    }
    
    setCurrentTimesheetDay(
      timesheetDayData?.data?.find((item) => item.id === event.id)
    );
    setSelectedDate(event.start);
    setSelectedEvent(event);

    if (hasExtraHour && (event.start.getDay() === 0 || event.start.getDay() === 6) && !userEmployee?.isSuportService)
      setShowExtraHourModal(true);
    else 
      setShowModal(true);
  };

  /**
   * Calcula e define as horas de início e fim dos eventos de um dia para exibição no calendário.
   * Garante que os eventos são exibidos sequencialmente a partir das 9h.
   * @param {EventType[] | undefined} eventsToFormat - A lista de eventos a ser processada.
   */
  const calculateDateTime = async (eventsToFormat: EventType[] | undefined) => {
    if (!eventsToFormat) return;

    let currentDate: Date;

    eventsToFormat.sort((a, b) => {
      return a.start.getTime() - b.start.getTime();
    });

    const eventsUpdated = eventsToFormat.map((item) => {
      if (item.title.includes("Deslocação") || item.title.includes("Prevenção"))
        return item;

      if (
        !currentDate ||
        item.start.getFullYear() !== currentDate.getFullYear() ||
        item.start.getMonth() !== currentDate.getMonth() ||
        item.start.getDate() !== currentDate.getDate()
      ) {
        item.start.setHours(9);
        item.end.setHours(item.start.getHours() + (item.hours ?? 0));
        currentDate = item.end;
        return item;
      }
      item.start.setHours(currentDate.getHours());
      item.end.setHours(currentDate.getHours() + (item.hours ?? 0));
      currentDate = item.end;
      return item;
    });
    return eventsUpdated;
  };

  /**
   * Calcula o número de dias no mês da timesheet atual.
   */
  const coundDaysInMonth = () => {
    const month31Days = [1, 3, 5, 7, 8, 10, 12];
    let days = 30;
    if (currentTimesheet) {
      days = month31Days.includes(currentTimesheet?.month) ? 31 : days;

      days =
        currentTimesheet.month === 2 && currentTimesheet.year % 4 === 0
          ? 29
          : days;

      days =
        currentTimesheet.month === 2 && currentTimesheet.year % 4 !== 0
          ? 28
          : days;
    }
    return days;
  };

  const countWokrDays = () => {
    const countDays = coundDaysInMonth();
    let workDays = 0;
    for (let day = 1; day <= countDays; day++) {
      if (currentTimesheet) {
        let currentDate = new Date(
          currentTimesheet?.year,
          currentTimesheet?.month - 1,
          day,
          0,
          0,
          0
        );

        if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6)
          workDays++;
      }
    }

    return workDays;
  };

  /**
   * Valida se a timesheet tem horas suficientes para ser concluída.
   */
  const validateTimesheet = (ts: Timesheet, events: EventType[]) => {
    const daysMonth = new Date(ts.year, ts.month, 0).getDate();

    for (let index = 1; index <= daysMonth; index++) {
      const newDate = new Date(ts.year, ts.month - 1, index, 12, 0, 0, 0);
      if (!userEmployee?.isSuportService && (newDate.getDay() === 6 || newDate.getDay() === 0)) 
          continue;
      
      const dayEvents = events.filter(
        (e) => e.start.getDate() === index && e.start.getMonth() + 1 === ts.month 
      );

      const totalDayHours = dayEvents.reduce(
        (sum, event) =>
          event.type !== 'extraHour'
            ? sum + (event.hours ?? 0)
            : sum,
        0
      );

      if (totalDayHours !== userEmployee?.workHoursPerDay) {

        let dayToShow = index.toString();
        let monthToShow = (ts.month).toString();
        if (index < 10)
          dayToShow = "0" + index.toString();
        if (ts.month < 10)
          monthToShow = "0" + (ts.month).toString();

        toast.current?.show({
          severity: "warn",
          summary: "Advertência",
          detail: `A soma das horas do dia ${dayToShow}/${monthToShow}/${ts.year} (${totalDayHours} horas) não é igual às suas horas diárias contratualizadas (${userEmployee?.workHoursPerDay} horas).`,
          life: 5000,
        });

        return false;
      }
    }

    return true;

    // let workDays = daysMonth;
    // let sumHours = 0;
    // const holidaysAndAbsences = events.filter(
    //   (e) => e.type === "abscence" && e.title.toLowerCase() === "feriado"
    // );
    // if (holidaysAndAbsences) workDays = workDays - holidaysAndAbsences.length;

    // if (!userEmployee?.isSuportService) {
    //   for (let index = 1; index <= daysMonth; index++) {
    //     const newDate = new Date(ts.year, ts.month - 1, index);
    //     if (newDate.getDay() === 6 || newDate.getDay() === 0)
    //       workDays = workDays - 1;
    //   }
    //   sumHours = events.reduce((acc, item) => {
    //     if (item.title.toLowerCase() === "feriado") return acc;

    //     console.log(item.title);
    //     if (abscenceTypesInDays.includes(item.title)) {
    //       acc = acc + userEmployee?.workHoursPerDay!;
    //       return acc;
    //     }
    //     acc = acc + item.hours;

    //     return acc;
    //   }, 0);

    //   if (workDays * userEmployee?.workHoursPerDay! <= sumHours) return true;
    //   toast.current?.show({
    //     severity: "warn",
    //     summary: "Advertência",
    //     detail: `Mapa de horas não concluído por falta de registos.`,
    //     life: 2000,
    //   });
    //   return false;
    // }

    // return true;
  };

  const handleClearTimesheet = async () => {
    if (!timesheetDayData?.data || timesheetDayData.data.length === 0) {
      toast.current?.show({
        severity: "info",
        summary: "Informação",
        detail: "Não existem registos para eliminar.",
        life: 2000,
      });
      return;
    }

    setIsProcessing(true);
    try {
      // 1. Cria um array de promessas, uma para cada registo a ser eliminado.
      const timesheetsWithoutHolidays = timesheetDayData.data.filter(
        (day) =>
          !day.abscenceType?.name ||
          !["Feriado", "Férias"].includes(day.abscenceType?.name)
      );

      const deletePromises = timesheetsWithoutHolidays.map((day) =>
        deleteTimesheetDay(day)
      );

      // 2. Executa todas as promessas em paralelo e espera que terminem.
      await Promise.all(deletePromises);

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: "Todos os registos foram eliminados.",
        life: 3000,
      });
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Ocorreu um erro ao eliminar os registos.",
        life: 3000,
      });
    } finally {
      // 3. Recarrega os dados para atualizar o calendário.
      await refetchTimesheetDays();
      setIsProcessing(false);
    }
  };

  /**
   * Manipulador para o botão "Concluir", que muda o status da timesheet para "Enviado".
   */
  const handleChangeStatus = async () => {
    if (currentTimesheet) {
      if (validateTimesheet(currentTimesheet, calendarEvents)) {
        const updatedTimesheet = {
          ...currentTimesheet,
          lastModifiedBy: userEmployeeEmail ?? EMAIL,
          status: "Enviado",
        };

        updateTimesheetMutation(updatedTimesheet, {
          onSuccess: (updatedData) => {
            // A mutação foi bem-sucedida, agora geramos o PDF em background.
            if (updatedData?.data) uploadPdfInBackground(currentTimesheet);
          },
        });
      }
    }
  };

  /**
   * Gera o PDF, faz o upload e cria o registo do documento em background.
   * @param {Timesheet} timesheet - O objeto timesheet já com o status "Enviado".
   */
  const uploadPdfInBackground = async (timesheet: Timesheet) => {
    // Força o refetch para garantir dados atualizados após mudança de status
    const { data: latestReportResponse } = await refetchReportData();
    
    const report = latestReportResponse?.data;
    const employee = employeeData?.data?.[0];
    const docTypeId = docTypeData?.data?.[0]?.id;

    if (!report || !employee || !docTypeId) {
      console.error("Dados insuficientes para gerar o PDF em background.", {
        report,
        employee,
        docTypeId,
      });
      toast.current?.show({
        severity: "warn",
        summary: "Aviso",
        detail: "Não foi possível gerar o anexo PDF por falta de dados.",
        life: 4000,
      });
      return;
    }

    try {
      // 1. Gerar o blob do PDF
      const blob = await pdf(
        <TimesheetReportPDF
          data={report}
          isSuportService={employee.isSuportService ?? false}
        />
      ).toBlob();
      /**
       * Busca documentos existentes para o mesmo período para posterior exclusão.
       */

      existingDocuments?.data?.map(async (doc) => {
        await deleteDocumentMutation(doc);
      });

      // 2. Chamar o hook para fazer o upload e criar o registo
      await processSingleReport({
        blob,
        report,
        employee,
        docTypeId,
      });
    } catch (error) {
      console.error("Erro ao gerar/guardar o PDF em background:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro no PDF",
        detail: "Ocorreu uma falha ao gerar o anexo PDF.",
        life: 4000,
      });
    }
  };

  /**
   * Converte uma string com quebras de linha (`\n`) em elementos JSX com `<br />`.
   */
  const convertNewLinesToJSX = (text: string) => {
    return text.split("\n").map((line, index) => (
      <span key={index}>
        {line}
        <br />
      </span>
    ));
  };

  /**
   * Exibe um diálogo de confirmação genérico.
   */
  const confirmAction = (message: string, acceptCallback: () => void) => {
    confirmDialog({
      message: <>{convertNewLinesToJSX(message)}</>,
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: acceptCallback,
    });
  };

  /**
   * Componente customizado para o cabeçalho de cada dia no calendário, que inclui o botão de adicionar.
   */
  const CustomDateHeader = ({ label, date }: { label: string; date: Date }) => {
    // Verificar se o dia tem férias nos dados da timesheet
    const hasFeriasEvent = timesheetDayData?.data?.some((day) => {
      const isSameDay = day.day === date.getDate();
      const isFeriasAbsence = day.abscenceType?.name?.toLowerCase() === "férias";
      return isSameDay && isFeriasAbsence;
    });

    const isFromOtherMonth = date.getMonth() + 1 !== currentTimesheet?.month;

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingLeft: "3px",
        }}
      >
        <span>{label}</span>
        {!isDisabled &&
          !userEmployee?.isSuportService &&
          !hasFeriasEvent &&
          !isFromOtherMonth &&
          date.getDay() !== 0 &&
          date.getDay() !== 6 && (
            <div>
              <Button
                icon="pi pi-plus"
                title="Adicionar novo registo"
                className="p-button-sm p-button-text"
                style={{ fontSize: "0.75rem", padding: "0px", width: "1.3rem" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddEvent(date);
                }}
              />
              <Button
                icon="pi pi-copy"
                title="Copiar registos para o próximo dia"
                className="p-button-sm p-button-text"
                style={{ fontSize: "0.75rem", padding: "0px", width: "1.3rem" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleReplyNextDay(date);
                }}
              />
            </div>
          )}
        {!isDisabled && userEmployee?.isSuportService && !hasFeriasEvent &&!isFromOtherMonth && (
          <div>
            <Button
              icon="pi pi-plus"
              title="Adicionar novo registo"
              className="p-button-sm p-button-text"
              style={{ fontSize: "0.75rem", padding: "0px", width: "1.3rem" }}
              onClick={(e) => {
                e.stopPropagation();
                handleAddEvent(date);
              }}
            />
            <Button
              icon="pi pi-copy"
              title="Copiar registos para o próximo dia"
              className="p-button-sm p-button-text"
              style={{ fontSize: "0.75rem", padding: "0px", width: "1.3rem" }}
              onClick={(e) => {
                e.stopPropagation();
                handleReplyNextDay(date);
              }}
            />
          </div>
        )}

        {hasExtraHour 
          && (date.getDay() === 0 || date.getDay() === 6)
          && !userEmployee?.isSuportService
          && (
          <div>
            <Button
              icon="pi pi-plus"
              title="Adicionar novo registo"
              className="p-button-sm p-button-text"
              style={{ fontSize: "0.75rem", padding: "0px", width: "1.3rem" }}
              onClick={(e) => {
                e.stopPropagation();
                handleAddExtraHourEvent(date);
              }}
            />
          </div>
        )}
      </div>
    );
  };

  /**
   * Função que retorna propriedades de estilo para cada evento no calendário.
   */
  const eventPropGetter = (
    event: EventType,
    start: Date,
    end: Date,
    isSelected: boolean
  ) => {
    let backgroundColor = "#3174ad";

    if (event.type === "abscence") {
      backgroundColor = "#c0c0c0";
    }

    const style = {
      backgroundColor: backgroundColor,
      borderRadius: "0px",
      opacity: 0.8,
      color: "white",
      border: "0px",
      display: "block",
    };

    return {
      style: style,
    };
  };

  return (
    <div className="calendar-container" style={{ position: "relative" }}>
      {(isLoading || isProcessing) && (
        <div className="spinner-overlay">
          <Spinner />
        </div>
      )}
      {!isLoading && !isProcessing && (
        <>
          <div className="default-grid header-spacing">
            <div className="default-1-col">
              <InputTextComponent
                label="Colaborador"
                fieldName="name"
                currentObject={currentTimesheetForm}
                getValue={currentTimesheetForm?.employee.name}
                isDisable={true}
                setValue={setCurrentTimesheetForm}
              />
            </div>
            <div className="default-1-col">
              <InputTextComponent
                label="Mês"
                fieldName="month"
                currentObject={currentTimesheetForm}
                getValue={
                  currentTimesheetForm?.month + "/" + currentTimesheetForm?.year
                }
                isDisable={true}
                setValue={setCurrentTimesheetForm}
              />
            </div>
            <div className="default-1-col">
              <InputTextComponent
                label="Status"
                fieldName="status"
                currentObject={currentTimesheetForm}
                getValue={currentTimesheetForm?.status}
                isDisable={true}
                setValue={setCurrentTimesheetForm}
              />
            </div>
          </div>

          <Calendar
            localizer={locale}
            events={calendarEvents}
            // onSelectSlot={(slotInfo) => handleAddEvent(slotInfo.start)}
            onSelectEvent={handleEdit}
            selectable
            date={currentDate}
            onNavigate={handleNavigate}
            className="calendar-height"
            views={["month", "week", "day"]}
            view={view}
            onView={handleViewChange}
            eventPropGetter={eventPropGetter}
            messages={messages}
            components={{
              month: {
                dateHeader: CustomDateHeader,
              },
            }}
          />
          <div className="default-4-cols button-align">
            {!isDisabled && (
              <>
                <Button
                  label="Limpar"
                  className="button-toolbar "
                  onClick={() =>
                    confirmAction(
                      "Deseja eliminar todos os registos desta timesheet?",
                      handleClearTimesheet
                    )
                  }
                />
                <Button
                  label="Concluir"
                  className="button-toolbar "
                  onClick={() =>
                    confirmAction(
                      "Deseja bloquear o mapa de horas deste mês?",
                      handleChangeStatus
                    )
                  }
                />
              </>
            )}
            <Button
              label="Sair"
              className="button-toolbar "
              onClick={() => {
                setCurrentEvents([]);
                setCalendarEvents([]);
                setSelectedEvent(undefined);
                setSelectedDate(null);
                resetCurrentTimesheetDay();
                resetTimesheetForm();
                setShowModal(false);
                setShowExtraHourModal(false);
                navigate(-1);
              }}
            />
          </div>
        </>
      )}
      <Dialog
        header="Registo de Horas"
        visible={showModal}
        style={{ width: "40vw" }}
        onHide={handleCloseModal}
      >
        <TimesheetDayForm
          currentDate={selectedDate}
          currentEvent={selectedEvent}
          calendarEvents={calendarEventsWithEmpty}
          isDisable={isDisabled || false}
          onSave={handleAdd}
          onDelete={handleDelete}
          setIsProcessing={setIsProcessing}
          setCalendarEvents={setCalendarEvents}
        />
      </Dialog>

      <Dialog
        header="Registo de Horas"
        visible={showExtraHourModal}
        style={{ width: "40vw" }}
        onHide={handleCloseExtraHourModal}
      >
        <ExtraHourTimesheetDayForm
          currentDate={selectedDate}
          currentEvent={selectedEvent}
          calendarEvents={calendarEventsWithEmpty}
          isDisable={isDisabled || false}
          onSave={handleAdd}
          onDelete={handleDelete}
          setIsProcessing={setIsProcessing}
          setCalendarEvents={setCalendarEvents}
        />
      </Dialog>

      <Toast ref={toast} position="bottom-right" />
      <ConfirmDialog />
    </div>
  );
};

export default TimesheetCalendar;
